import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "~/components/ui/use-toast";
import type { TPartImage } from "~/database/types";
import { useApiClient } from "~/hooks/use-api-client";

// Type for part image with signed URL (copied from modal)

// Type for the mutation input variables
interface DeletePartImageVariables {
    partId: string;
    imageId: string;
}

// Type for the expected API response
interface DeletePartImageResponse {
    success: boolean;
}
interface PartImageWithSignedUrl extends TPartImage {
    signedUrl: string | null;
}

/**
 * Hook to delete a part image.
 */
export function useDeletePartImage() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation<
        DeletePartImageResponse,
        Error,
        DeletePartImageVariables
    >({
        mutationFn: async ({ partId, imageId }) => {
            // AIDEV-NOTE: partId now sent as query param for DELETE since apiClient.delete doesn't support body
            const response = await apiClient.delete(`/part/image/${imageId}`, {
                params: { partId },
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    // Ignore
                }
                const errorMessage =
                    errorData?.error ||
                    `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            // DELETE requests might return 204 No Content or 200 OK with a body
            // If there's a body, parse it, otherwise assume success based on status code.
            if (response.status === 204) {
                return { success: true };
            }
            return response.json() as Promise<DeletePartImageResponse>;
        },
        onSuccess: (data, variables) => {
            // Remove the deleted image from the list query cache by filtering it out
            queryClient.setQueryData<PartImageWithSignedUrl[]>(
                ["partImages", variables.partId],
                (oldImages) =>
                    oldImages?.filter((img) => img.id !== variables.imageId) ||
                    [],
            );

            // Remove the deleted image's status cache
            queryClient.removeQueries({
                queryKey: [
                    "partImageStatus",
                    variables.partId,
                    variables.imageId,
                ],
            });
            // Invalidate the part query in case this was the default image
            queryClient.invalidateQueries({
                queryKey: ["part", variables.partId],
            });

            toast({
                title: "Image Deleted",
                description: "The image has been successfully deleted.",
            });
        },
        onError: (error) => {
            toast({
                title: "Deletion Failed",
                description: error.message || "Could not delete image.",
                variant: "destructive",
            });
        },
    });
}
